// Multi-pedigree-tabs — open-set persistence.
//
// Persists the OPEN SET (which pedigrees are open, their tab order, mode, and
// which is active/preview) to sessionStorage so a page refresh in the same
// browser tab restores the tabs. Deliberately sessionStorage, not localStorage:
// window.localStorage.clear() is called elsewhere (e.g. Questionnaire) and would
// wipe it, and the open set is naturally per-browser-tab.
//
// Stores the durable fields — NOT the transient lock state or unsaved flag. The
// per-tab viewState (selected node / expanded section / camera) IS persisted so a
// refresh can restore where each tab was left (FU9).

export const WORKSPACE_STORAGE_KEY = 'tgc:mpt:workspace:v1';

// Reduce live state to the minimal durable snapshot.
export function serializeWorkspace(state) {
  if (!state || !Array.isArray(state.sessions)) return null;
  return {
    v: 1,
    sessions: state.sessions.map((s) => ({
      sessionId: s.sessionId,
      familyId: s.familyId,
      title: s.title,
      mode: s.mode,
      viewState: s.viewState || null
    })),
    activeSessionId: state.activeSessionId,
    previewSessionId: state.previewSessionId
  };
}

// Rebuild a RESTORE payload (full session shape with transient fields reset).
export function deserializeWorkspace(raw) {
  if (!raw || !Array.isArray(raw.sessions)) return null;
  const sessions = raw.sessions
    .filter((s) => s && s.sessionId && s.familyId)
    .map((s) => ({
      sessionId: s.sessionId,
      familyId: s.familyId,
      title: s.title || s.familyId,
      probandName: '',
      mode: s.mode === 'pinned' ? 'pinned' : 'preview',
      lock: 'none',
      hasUnsavedChanges: false,
      inaccessible: false,
      releaseNonce: 0,
      lastActiveAt: 0,
      // Restore the per-tab view state (selected node / section / camera) so a
      // refresh returns each tab to where it was left.
      viewState: s.viewState || null
    }));
  if (sessions.length === 0) return null;
  const ids = new Set(sessions.map((s) => s.sessionId));
  return {
    sessions,
    activeSessionId: ids.has(raw.activeSessionId) ? raw.activeSessionId : null,
    previewSessionId: ids.has(raw.previewSessionId)
      ? raw.previewSessionId
      : null
  };
}

function safeSessionStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function loadWorkspace(storage = safeSessionStorage()) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(WORKSPACE_STORAGE_KEY);
    if (!raw) return null;
    return deserializeWorkspace(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveWorkspace(state, storage = safeSessionStorage()) {
  if (!storage) return;
  try {
    const snapshot = serializeWorkspace(state);
    if (!snapshot || snapshot.sessions.length === 0) {
      storage.removeItem(WORKSPACE_STORAGE_KEY);
      return;
    }
    storage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* quota / disabled storage — persistence is best-effort */
  }
}
